import { OPEN_GROUND_TILE, TERRAIN_TILES, type EncounterGround, type TileTypeId } from "@lancer-vtt/domain";
import { tilePaletteGroups } from "../../../routes/home/terrainTiles";
import { useSwatchUrl } from "../../../routes/home/terrainArt/useSwatch";

/** A tile's painted swatch beside its name (`terrain-tile-art` 5.4): the art the brush will paint.
 *  Open ground's is the bare map ground. Decorative: the name says which tile it is. */
function PaletteSwatch({ id, ground }: { id: TileTypeId; ground?: EncounterGround }) {
  const url = useSwatchUrl({ kind: "tile", cell: { tile: id }, shape: "chip", ground });
  return <span className="cw-palette-swatch" aria-hidden="true">{url && <img src={url} alt="" />}</span>;
}

/** The palette's groups, computed once: the catalogue is a code constant. */
const GROUPS = tilePaletteGroups();

/**
 * The TERRAIN window's **tile palette** (`projects/terrain-tile-types.md` Phase 4): every catalogue
 * tile as a button showing its painted swatch, name and property chips, grouped by what the tile does (cover,
 * difficult ground, impassable, dangerous). Picking one arms the tile brush with it, and painting
 * writes `{ tile: id }` cells through the ordinary `set_terrain` batch (`decisions/0185`). "Open
 * ground" is the clearing tile. Picking the armed tile again disarms it, like the tool rail.
 *
 * The chips are what make the catalogue's same-mechanics pairs readable: "Trees" and "Boulders"
 * both read `hard cover`, so the GM picks by fiction knowing the two play the same.
 *
 * Issues no command itself; the stage paints with the armed tile.
 */
export function TilePalette({
  armed,
  disabled,
  ground,
  onPick,
  onDisarm,
}: {
  /** The tile the armed tile brush paints, or `null` when the tile brush isn't armed. */
  armed: TileTypeId | null;
  disabled: boolean;
  /** The map ground the swatches are painted on (the encounter's own). */
  ground?: EncounterGround;
  onPick: (tile: TileTypeId) => void;
  onDisarm: () => void;
}) {
  const pick = (id: TileTypeId) => (armed === id ? onDisarm() : onPick(id));
  const openOn = !disabled && armed === OPEN_GROUND_TILE;
  return (
    <section className="cw-tool-section cw-palette" aria-label="tile palette">
      <div className="cw-section-name">Tiles</div>
      <button
        type="button"
        className={`cw-palette-tile cw-palette-open${openOn ? " cw-palette-tile-on" : ""}`}
        data-tile-id={OPEN_GROUND_TILE}
        disabled={disabled}
        aria-pressed={openOn}
        title={`${TERRAIN_TILES[OPEN_GROUND_TILE].blurb} Painting it clears a cell.`}
        onClick={() => pick(OPEN_GROUND_TILE)}
      >
        <PaletteSwatch id={OPEN_GROUND_TILE} ground={ground} />
        <span className="cw-palette-name">{TERRAIN_TILES[OPEN_GROUND_TILE].name}</span>
        <span className="cw-palette-chips">
          <span className="cw-palette-chip">clears</span>
        </span>
      </button>
      {GROUPS.map((group) => (
        <div key={group.id} className="cw-palette-group" role="group" aria-label={group.label} data-tile-group={group.id}>
          <div className="cw-palette-group-name">{group.label}</div>
          <div className="cw-palette-grid">
            {group.tiles.map((tile) => {
              const on = !disabled && armed === tile.id;
              return (
                <button
                  key={tile.id}
                  type="button"
                  className={`cw-palette-tile${on ? " cw-palette-tile-on" : ""}`}
                  data-tile-id={tile.id}
                  disabled={disabled}
                  aria-pressed={on}
                  title={tile.blurb}
                  onClick={() => pick(tile.id)}
                >
                  <PaletteSwatch id={tile.id} ground={ground} />
                  <span className="cw-palette-name">{tile.name}</span>
                  <span className="cw-palette-chips">
                    {tile.chips.map((chip) => (
                      <span key={chip} className="cw-palette-chip">
                        {chip}
                      </span>
                    ))}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
}
